import { apiJson } from './api'

const SETTINGS_KEY = 'oc_settings'

export const DEFAULT_SETTINGS = {
  // a leg counts as a value bet when the soft price beats the sharp one by at least this much, in percent
  minYield: 6,
  // a leg counts as an arb when backing it at the soft book and hedging the rest at the sharp book
  // locks in at least this much profit, in percent of the stake
  minArbProfit: 0.5,
  // sports the feed carries but this browser does not want to see anywhere in the menu
  hiddenSports: [],
  // individual leagues switched off, keyed "Sport|League" because league names repeat across sports
  hiddenLeagues: [],
  // how many events in a league currently hold an arb, shown next to it in the Arbs menu
  showCounts: true,
  // off until somebody asks for it: a sound nobody chose is the most annoying kind
  notificationSound: false,
  // the compact favourites board, cards several to a row rather than one long table
  favoritesBoard: true,
  // which clock every screen prints: 'UTC' or 'local'. UTC is what the feeds and the database speak,
  // and one zone across a desk that spans several is worth more than each machine's own idea of now
  timeZone: 'UTC',
  // and raises a desktop notification, which is a separate decision from the sound
  desktopNotifications: true,
  // where a bot would send the same alerts; nothing sends yet, this only remembers the destination
  telegramChatId: ''
}

export const leagueKey = (sport, league) => `${sport}|${league}`

/**
 * Whatever we were handed, made safe to render. Settings reach the app through three doors — this
 * browser's cache, the login answer and /api/me — and an account saved before a setting existed simply
 * has no such key. Every door goes through here, so a screen can read settings.hiddenSports without
 * wondering whether this particular account is old enough to be missing it.
 */
export const withDefaults = (raw) => {
  const merged = { ...DEFAULT_SETTINGS, ...(raw && typeof raw === 'object' ? raw : {}) }
  // an older saved shape has no list at all, and the rest of the app indexes into it
  if (!Array.isArray(merged.hiddenSports)) {
    merged.hiddenSports = []
  }
  if (!Array.isArray(merged.hiddenLeagues)) {
    merged.hiddenLeagues = []
  }
  return merged
}

export const loadSettings = () => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY)
    return withDefaults(raw ? JSON.parse(raw) : null)
  } catch (e) {
    return { ...DEFAULT_SETTINGS }
  }
}

export const clearSettings = () => {
  try {
    localStorage.removeItem(SETTINGS_KEY)
  } catch (e) {}
}

export const saveSettings = (settings) => {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings))
  } catch (e) {}
}

/**
 * The server is where settings actually live, keyed by the session id in the cookie, so the same browser
 * keeps its thresholds and its sports across machines it is signed in on. localStorage is only a cache:
 * it paints the first frame without waiting for a round trip and covers the server being unreachable.
 */
export const fetchSettings = async (host) => {
  const data = await apiJson(host, '/api/settings')
  if (!data.settings) {
    return null
  }
  const merged = withDefaults(data.settings)
  if (!Array.isArray(merged.hiddenSports)) {
    merged.hiddenSports = []
  }
  if (!Array.isArray(merged.hiddenLeagues)) {
    merged.hiddenLeagues = []
  }
  return merged
}

export const pushSettings = async (host, settings) => {
  await apiJson(host, '/api/settings', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ settings })
  })
}
